import { AspectRatio, Box, BoxProps, Container, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'

import { getBrand } from '@lib/util/brand-handler'
import BrandLayout from '@layouts/brand-layout'
import Head from '@modules/common/components/head'
import Hero from './hero'
import Problem from './problem'

import Material from './material'
import Stability from './stability'
import Image from 'next/image'
import Insulation from './insulation'
import Technology from './technology'
import Guarantee from './guarantee'
import Design from './design'
import QA from './qa'
import Review from './review'
import GripTumblerBelieve from './grip-tumbler-believe'
import InsulationMobile from './insulation-mobile-tablet'
import ExclusiveOffer from './exclusive-offer'
import useAosAnimation from '@common/hooks/use-aos-animation'

const imageDatas = {
  bgHero:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-hero-1711631563819.png',
  bgCheck:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-check-1711631730491.png',
  bgMaterial:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-material-1711631730495.png',
  bgLaptop:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-lap-1711963689049.png',
  bgDouTumbler:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-dou-tumbler-1711963689047.png',
  bgTechnology:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-technology-1711631832584.png',
  bgGuarantee:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-guarantee-1711631832582.png',
  bgDesign:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-design-1711631903040.png',
  bgReview:
    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-review-1711631903041.png',
  bgQa: 'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-qa-1711955976218.png'
}

export const CONTAINER_WIDTH = {
  LARGE: 990,
  MEDIUM: 760
}

interface BgWrapperProps {
  url: string
  children?: React.ReactNode
}

const BgWrapper = ({ url, children, ...props }: BgWrapperProps & BoxProps) => {
  return (
    <Box
      bgImage={`url(${url})`}
      w="100%"
      bgSize="cover"
      bgPosition="center"
      {...props}
    >
      {children}
    </Box>
  )
}

export const MainContainer = (props: BoxProps) => {
  return (
    <Container
      maxW={{
        base: CONTAINER_WIDTH.MEDIUM,
        lg: CONTAINER_WIDTH.LARGE
      }}
      {...props}
    />
  )
}

const GripTumbler = () => {
  const { data } = useQuery(['brand', 'arctic'], () => getBrand('arctic'))

  useAosAnimation({
    duration: 600,
    once: false
  })

  if (!data) return null

  const { title, metadata, productSection, color } = data

  return (
    <>
      <Head title={title} description={metadata.meta_descrtiption} />
      <BrandLayout
        name={title}
        color={color || '#E31C5A'}
        brand={data}
        productSection={productSection}
      >
        <VStack w="100%" alignItems="center" gap={0}>
          {/* <Box maxW="1024px" w="100%"> */}
          <BgWrapper data-aos="fade-up" url={imageDatas.bgHero}>
            <MainContainer>
              <Hero />
            </MainContainer>
          </BgWrapper>

          <BgWrapper
            data-aos="fade-up"
            url={imageDatas.bgCheck}
            position="relative"
            h={{
              base: 'auto',
              lg: '447px',
              xl: '549px'
            }}
          >
            <MainContainer>
              <Problem />
            </MainContainer>

            <VStack
              top={0}
              right={0}
              position="absolute"
              mt="28px"
              display={{
                base: 'none',
                lg: 'flex'
              }}
            >
              <AspectRatio
                w={{
                  base: '180px',
                  sm: '260px',
                  md: '300px',
                  lg: '400px',
                  xl: '500px'
                }}
                ratio={1 / 1}
                position="relative"
                zIndex={10}
                mb={4}
              >
                <Image
                  src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/tumbler-check-1711632565104.png"
                  alt="tumbler check"
                  fill
                />
              </AspectRatio>

              <VStack
                position="absolute"
                top={{
                  base: '80%',
                  sm: '83%',
                  md: '83%',
                  lg: '83%'
                }}
                left={{
                  md: '-74px',
                  lg: 0
                }}
                alignItems="flex-start"
              >
                <AspectRatio
                  w={{
                    base: '180px',
                    sm: '260px',
                    md: '300px',
                    lg: '400px',
                    xl: '500px'
                  }}
                  ratio={480 / 90}
                  position="relative"
                >
                  <Image
                    src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/wood-1711632565102.png"
                    alt="wood"
                    fill
                  />
                </AspectRatio>
              </VStack>

              <VStack
                position="absolute"
                top={{
                  base: '70%',
                  sm: '70%',
                  md: '65%'
                }}
                left="47%"
                alignItems="flex-start"
                zIndex={10}
              >
                <AspectRatio
                  mt={0}
                  w={{
                    base: '40px',
                    sm: '40px',
                    md: '80px',
                    lg: '90px',
                    xl: '120px'
                  }}
                  ratio={1 / 1}
                  position="relative"
                >
                  <Image
                    src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/icon-check-1711632565104.png"
                    alt="icon check"
                    fill
                  />
                </AspectRatio>
              </VStack>
            </VStack>
          </BgWrapper>

          <Box
            w="100%"
            bgColor="#fbfafa"
            bgImage="linear-gradient(to bottom, #fff, #dbdbdb)"
          >
            <MainContainer>
              <ExclusiveOffer />
            </MainContainer>
          </Box>

          <BgWrapper
            data-aos="fade-up"
            url={imageDatas.bgMaterial}
            position="relative"
            h={{
              base: 'auto',
              lg: '615px',
              xl: '754px'
            }}
          >
            <MainContainer>
              <Material />
            </MainContainer>

            <VStack
              data-aos="fade-up"
              top={{
                base: 0,
                lg: '40px'
              }}
              right={0}
              mt={{
                base: 0,
                sm: '20px'
              }}
              position="absolute"
              display={{
                base: 'none',
                lg: 'flex'
              }}
            >
              <AspectRatio
                w={{
                  base: '200px',
                  sm: '240px',
                  md: '300px',
                  lg: '400px',
                  xl: '500px'
                }}
                ratio={330 / 460}
                position="relative"
                zIndex={10}
              >
                <Image
                  src="https://habbit-sg.s3.ap-southeast-1.amazonaws.com/tumbler-material-1711632721464.png"
                  alt="tumbler material"
                  fill
                />
              </AspectRatio>
            </VStack>
          </BgWrapper>

          <AspectRatio
            data-aos="fade-up"
            w="100%"
            ratio={1024 / 568}
            position="relative"
          >
            <>
              <Image src={imageDatas.bgLaptop} alt="bg laptop" fill />
              <Stability />
            </>
          </AspectRatio>

          <AspectRatio
            data-aos="fade-up"
            w="100%"
            ratio={1024 / 608}
            position="relative"
            display={{
              base: 'none',
              md: 'block'
            }}
          >
            <>
              <Image src={imageDatas.bgDouTumbler} alt="dou tumbler" fill />
              <Insulation />
            </>
          </AspectRatio>

          <BgWrapper
            data-aos="fade-up"
            url={imageDatas.bgMaterial}
            display={{
              base: 'block',
              md: 'none'
            }}
            bgPosition="top"
          >
            <MainContainer>
              <InsulationMobile />
            </MainContainer>
          </BgWrapper>

          <BgWrapper data-aos="fade-up" url={imageDatas.bgTechnology}>
            <MainContainer>
              <Technology />
            </MainContainer>
          </BgWrapper>

          <BgWrapper data-aos="fade-up" url={imageDatas.bgGuarantee}>
            <MainContainer>
              <Guarantee />
            </MainContainer>
          </BgWrapper>

          <BgWrapper data-aos="fade-up" url={imageDatas.bgDesign}>
            <MainContainer>
              <Design />
            </MainContainer>
          </BgWrapper>

          <BgWrapper data-aos="fade-up" url={imageDatas.bgQa}>
            <MainContainer>
              <Review />
            </MainContainer>
          </BgWrapper>

          <Box w="100%" bgImage="linear-gradient(to bottom, #fff, #dbdbdb)">
            <GripTumblerBelieve />
          </Box>

          <BgWrapper data-aos="fade-up" url={imageDatas.bgQa} pt={20} pb={20}>
            <MainContainer>
              <QA />
            </MainContainer>
          </BgWrapper>
          {/* </Box> */}
        </VStack>
      </BrandLayout>
    </>
  )
}

export default GripTumbler
